// Feature theming — every conversational-form surface takes color from the
// tenant config contract only (`PrimaryColor` → accent, `SecondaryColor` →
// secondary), with tints derived the way production derives them (MUI
// lighten/darken, theme.ts) so a two-variable re-skin needs no per-component
// override. No hard-coded brand colors live in this feature.
//
// The runtime on-accent flip is the safety net behind the config-time contrast
// rule (handoff: "require tenant accents ≥ 4.5:1 against white; the runtime flip
// is the fallback, not the policy").

import { alpha, darken, lighten } from '@mui/material/styles';
import { CSSProperties } from 'react';
import { EdullaConfig } from '../../config/configType';

/** Relative luminance per WCAG 2.x. Returns 0 for anything unparseable so an
 *  unreadable config degrades to the white-on-accent production default. */
export const accentLuminance = (hex: string): number => {
    const raw = String(hex || '').trim().replace('#', '');
    if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) return 0;
    const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
    const n = parseInt(full, 16);
    const channel = (c: number) => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
};

export const ON_ACCENT_LIGHT = { text: '#fffff2', dim: 'rgba(255,255,242,.82)' };
export const ON_ACCENT_DARK = { text: '#1b1a17', dim: 'rgba(27,26,23,.72)' };

/** Flip on-accent text/icons to near-black when the tenant accent is light. */
export const onAccentColors = (accent: string): { text: string; dim: string } =>
    accentLuminance(accent) > 0.35 ? ON_ACCENT_DARK : ON_ACCENT_LIGHT;

const safeTint = (color: string, fn: (c: string, amount: number) => string, amount: number, fallback: string) => {
    try {
        return fn(color, amount);
    } catch {
        return fallback;
    }
};

export type FormsTheme = {
    accent: string;
    secondary: string;
    onAccent: string;
    onAccentDim: string;
    /** 10%-of-accent tint — card fills, icon tiles. */
    soft: string;
    /** 30%-of-accent tint — hairlines and receipt borders. */
    line: string;
    accentDark: string;
    /** Secondary-derived ramp used for the AI-review (suggestion) treatment. */
    reviewSoft: string;
    reviewLine: string;
    radius: string;
    shadow: string;
    fontFamily: string;
};

const FALLBACK_ACCENT = '#003876';
const FALLBACK_SECONDARY = '#836EAA';

export const buildFormsTheme = (config: EdullaConfig): FormsTheme => {
    const accent = config.PrimaryColor || FALLBACK_ACCENT;
    const secondary = config.SecondaryColor || FALLBACK_SECONDARY;
    const on = onAccentColors(accent);

    return {
        accent,
        secondary,
        onAccent: on.text,
        onAccentDim: on.dim,
        soft: safeTint(accent, lighten, 0.9, alpha(FALLBACK_ACCENT, 0.1)),
        line: safeTint(accent, lighten, 0.7, alpha(FALLBACK_ACCENT, 0.3)),
        accentDark: safeTint(accent, darken, 0.3, FALLBACK_ACCENT),
        reviewSoft: safeTint(secondary, lighten, 0.9, alpha(FALLBACK_SECONDARY, 0.1)),
        reviewLine: safeTint(secondary, lighten, 0.6, alpha(FALLBACK_SECONDARY, 0.4)),
        radius: `${config.BorderRadiusNumber ?? 10}px`,
        shadow: config.BoxShadow || 'rgba(150,150,150,.8) 0px 10px 20px -10px',
        fontFamily: config.FontFamily || 'inherit',
    };
};

/**
 * CSS custom properties for the feature root. Components read `var(--cf-*)`
 * rather than props so nested surfaces re-theme with the two config colors alone.
 */
export const formsThemeVars = (theme: FormsTheme): CSSProperties =>
    ({
        '--cf-accent': theme.accent,
        '--cf-secondary': theme.secondary,
        '--cf-on-accent': theme.onAccent,
        '--cf-on-accent-dim': theme.onAccentDim,
        '--cf-soft': theme.soft,
        '--cf-line': theme.line,
        '--cf-accent-dark': theme.accentDark,
        '--cf-review-soft': theme.reviewSoft,
        '--cf-review-line': theme.reviewLine,
        '--cf-radius': theme.radius,
        '--cf-shadow': theme.shadow,
        '--cf-font': theme.fontFamily,
    } as CSSProperties);
